/*
 * Drag to scroll, for a mouse. Originally ported from the Moonless rebuild's
 * moonless-dragscroll.js, which David asked to reuse here, then reworked for smoothness.
 *
 * Every horizontal rail on this site (category tabs, size rows, cart upsell) is a plain
 * overflow-x container. On a phone that is fine: you swipe it and it moves. With a mouse it is
 * dead, so this adds press-and-drag to anything marked [data-pf-drag].
 *
 * Why it is frame-based rather than writing scrollLeft straight from pointermove: pointermove
 * can fire several times per frame, and every write forces the browser to reconcile scroll
 * position mid-frame. That is what made it feel steppy. Moves now only record a target and a
 * single requestAnimationFrame applies it, so there is one smooth update per frame, and the
 * release glide runs on the same loop.
 */
(function () {
	'use strict';

	/* Global: stop native image/link drag inside any carousel/slider/banner so a press-drag
	   swipes the track instead of ghosting the picture (owner 2026-08-26). */
	document.addEventListener('dragstart', function (e) {
		var t = e.target;
		if (t && t.closest && t.closest('.splide, .carousel-inner, [data-pf-drag], .collection-banner')) {
			e.preventDefault();
		}
	}, true);

	var THRESHOLD = 4; /* px of movement before a press counts as a drag */
	var FRICTION = 0.94; /* per-frame decay of the release glide */
	var MIN_GLIDE = 0.4; /* px/frame below which the glide stops */

	function bind(rail) {
		if (rail.dataset.pfDragBound) return;
		rail.dataset.pfDragBound = '1';

		/* Splide binds its own drag on the track and sees the event before this rail does, so
		   stopPropagation alone does not hold. Switch its Drag component off while the pointer
		   is inside this rail, then switch it back on. */
		var splideEl = rail.closest('.splide');

		function setCarouselDrag(enabled) {
			if (!splideEl) return;
			var inst = splideEl.splide;
			var drag = inst && inst.Components && inst.Components.Drag;
			if (!drag) return;
			if (enabled) {
				if (drag.enable) drag.enable();
				else if (drag.disable) drag.disable(false);
			} else if (drag.disable) {
				drag.disable(true);
			}
		}

		rail.addEventListener('pointerenter', function () { setCarouselDrag(false); });
		rail.addEventListener('pointerleave', function () { setCarouselDrag(true); });
		rail.addEventListener('touchstart', function () { setCarouselDrag(false); }, { passive: true });
		rail.addEventListener('touchend', function () { setCarouselDrag(true); }, { passive: true });

		/* Touch and native image drag are swallowed so they never reach a parent carousel.
		   pointerdown and mousedown deliberately are NOT: the drag handlers below are bubble
		   listeners on this same element, so stopping those at capture would stop the rail
		   ever seeing its own events. */
		['touchstart', 'dragstart'].forEach(function (type) {
			rail.addEventListener(type, function (event) { event.stopPropagation(); }, { capture: true });
		});
		rail.addEventListener('touchmove', function (event) { event.stopPropagation(); }, { capture: true, passive: true });

		var down = false;
		var moved = false;
		var startX = 0;
		var startScroll = 0;
		var target = 0;
		var lastX = 0;
		var lastT = 0;
		var velocity = 0;
		var frame = null;

		function maxScroll() {
			return rail.scrollWidth - rail.clientWidth;
		}

		function apply() {
			frame = null;
			var max = maxScroll();
			if (target < 0) target = 0;
			if (target > max) target = max;
			rail.scrollLeft = target;
		}

		function schedule() {
			if (frame === null) frame = requestAnimationFrame(apply);
		}

		function glide() {
			velocity *= FRICTION;
			if (Math.abs(velocity) < MIN_GLIDE) return;
			var max = maxScroll();
			target += velocity;
			if (target <= 0 || target >= max) {
				/* hit an end: settle there rather than grinding against it */
				target = target <= 0 ? 0 : max;
				rail.scrollLeft = target;
				return;
			}
			rail.scrollLeft = target;
			requestAnimationFrame(glide);
		}

		rail.addEventListener('pointerdown', function (event) {
			if (event.pointerType !== 'mouse' || event.button !== 0) return;
			down = true;
			moved = false;
			startX = event.clientX;
			startScroll = rail.scrollLeft;
			target = rail.scrollLeft;
			lastX = event.clientX;
			lastT = event.timeStamp;
			velocity = 0;
		});

		rail.addEventListener('pointermove', function (event) {
			if (!down) return;
			var dx = event.clientX - startX;
			if (!moved && Math.abs(dx) < THRESHOLD) return;

			if (!moved) {
				moved = true;
				rail.classList.add('is-dragging');
				if (rail.setPointerCapture) {
					try { rail.setPointerCapture(event.pointerId); } catch (e) {}
				}
			}

			target = startScroll - dx;
			schedule();

			var dt = event.timeStamp - lastT;
			if (dt > 0) {
				/* smoothed, so one jittery sample cannot throw the glide */
				var vx = (lastX - event.clientX) / dt;
				velocity = velocity * 0.7 + vx * 0.3;
				lastX = event.clientX;
				lastT = event.timeStamp;
			}
			event.preventDefault();
		});

		function end(event) {
			if (!down) return;
			down = false;
			rail.classList.remove('is-dragging');
			if (rail.releasePointerCapture && event && event.pointerId != null) {
				try { rail.releasePointerCapture(event.pointerId); } catch (e) {}
			}
			if (moved) {
				velocity = velocity * 16; /* px/ms to px/frame at 60fps */
				requestAnimationFrame(glide);

				/* eat the click this drag is about to produce, so dragging the size row does
				   not navigate to whichever chip happened to be under the cursor */
				rail.addEventListener('click', function (click) {
					click.stopPropagation();
					click.preventDefault();
				}, { capture: true, once: true });
			}
		}

		rail.addEventListener('pointerup', end);
		rail.addEventListener('pointercancel', end);
		rail.addEventListener('pointerleave', end);
	}

	/* Explicit markers bind unconditionally, including inside carousels. The auto list picks up
	   rails the theme renders that we do not own, and skips Splide tracks, which drag
	   themselves. */
	var AUTO = '.pf-rail__list, .pf-card-sizes__groups, .pf-card-sizes__panel, .pf-size-tabs,'
		+ ' .size-buttons, .offcanvas-cart-upsell, .color-swatches, .cart-goal-icons,'
		+ ' .cards-with-image .row, .cards-with-image ul, .cart-upsell-list-horizontal';

	function scan() {
		document.querySelectorAll('[data-pf-drag]').forEach(bind);
		document.querySelectorAll(AUTO).forEach(function (el) {
			if (el.closest('.splide')) return;
			/* No overflow test here any more.

			   The cart drawer's recommendation rail is in the DOM from page load but inside a
			   closed offcanvas, so it measures 0 wide and 0 scrollWidth. The old test asked
			   whether it overflowed, got 0 > 2, and skipped it. Opening the drawer only toggles
			   a class, and the observer below watches childList, so nothing ever rescanned it
			   and the rail was never draggable on desktop.

			   Binding unconditionally is safe: a rail with nothing to scroll simply does not
			   move when dragged. */
			bind(el);
		});
	}

	if (document.readyState !== 'loading') scan();
	else document.addEventListener('DOMContentLoaded', scan);

	/* The cart drawer and the recommendation rows replace their markup after load. */
	new MutationObserver(scan).observe(document.documentElement, {
		childList: true,
		subtree: true,
	});

	/* Opening an offcanvas changes a class, not the markup, so the observer above never sees
	   it. Bootstrap announces it, and that is the moment a hidden rail first has a width. */
	document.addEventListener('shown.bs.offcanvas', scan);
	document.addEventListener('shown.bs.modal', scan);
})();
